import React from 'react';

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          
          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
              <div className="space-y-3">
                <div>
                  <h3 className="font-medium">Account Information</h3>
                  <p>We collect information you provide when creating an account, including name, email address, and organization details.</p>
                </div>
                <div>
                  <h3 className="font-medium">Usage Data</h3>
                  <p>We collect information about how you use our service, including session data, feature usage, and performance metrics.</p>
                </div>
                <div>
                  <h3 className="font-medium">Video Content</h3>
                  <p>During support sessions, video streams are transmitted through our secure servers but are not recorded or stored unless explicitly enabled and consented to by all parties.</p>
                </div>
                <div>
                  <h3 className="font-medium">Technical Information</h3>
                  <p>We collect device information, IP addresses, browser types, and other technical data necessary for service operation.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Provide and maintain our service</li>
                <li>Process payments and manage subscriptions</li>
                <li>Communicate with you about your account and service updates</li>
                <li>Improve our service and develop new features</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Video Session Privacy</h2>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">Key Privacy Protections:</h3>
                <ul className="list-disc pl-6 space-y-1 text-blue-800">
                  <li>All video sessions are encrypted end-to-end</li>
                  <li>Video content is not recorded by default</li>
                  <li>Sessions are automatically terminated when completed</li>
                  <li>Only authorized participants can join sessions</li>
                  <li>Session codes expire after use or timeout</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Information Sharing</h2>
              <p>We do not sell, trade, or otherwise transfer your personal information to third parties except:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li><strong>Service Providers:</strong> Trusted third parties who assist in operating our service (payment processing, hosting, analytics)</li>
                <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process</li>
                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                <li><strong>Consent:</strong> When you explicitly consent to sharing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Security</h2>
              <p>We implement industry-standard security measures including:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and monitoring</li>
                <li>Access controls and authentication</li>
                <li>Secure data centers and infrastructure</li>
                <li>Employee training on data protection</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to provide our services and fulfill legal obligations. 
                Account data is retained while your account is active and for a reasonable period afterward. 
                Video session metadata is retained for billing and support purposes but video content is not stored.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Your Rights</h2>
              <p>Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Delete your account and associated data</li>
                <li>Restrict or object to processing</li>
                <li>Data portability</li>
                <li>Withdraw consent</li>
              </ul>
              <p className="mt-3">To exercise these rights, contact us at privacy@streamshare.app</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Cookies and Tracking</h2>
              <p>
                We use cookies and similar technologies to enhance your experience, analyze usage patterns, and provide personalized content. 
                You can manage cookie preferences through your browser settings, though some features may not function properly with cookies disabled.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. International Data Transfers</h2>
              <p>
                Your information may be processed in countries other than your own. We ensure appropriate safeguards are in place 
                when transferring data internationally, including standard contractual clauses and adequacy decisions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Children's Privacy</h2>
              <p>
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information 
                from children under 13. If you become aware that a child has provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy periodically. We will notify you of any material changes via email or 
                service notification. We encourage you to review this policy regularly.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Contact Us</h2>
              <p>If you have any questions about this Privacy Policy, please contact us:</p>
              <div className="mt-2 p-4 bg-gray-50 rounded">
                <p>Email: privacy@streamshare.app</p>
                <p>Support: support@streamshare.app</p>
                <p>Website: https://streamshare.app</p>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 text-sm text-gray-500">
            <p>Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
